import { IOsiPaymentsWidgetViewModel } from '@shared/types/mobx/widgets/OsiPayments';
import { inject, injectable } from 'inversify';
import { makeAutoObservable, reaction } from 'mobx';
import moment from 'moment';
import api from '@app/api';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import axios from 'axios';
import { MIME, downloadBase64 } from '@shared/utils/download';
import { getGetApiTransactionsPaymentsOsiIdQueryOptions } from '@/shared/api/orval/transactions/transactions';
import { queryClient } from '@/shared/api/reactQuery';

@injectable()
export class OsiPaymentsWidgetViewModel implements IOsiPaymentsWidgetViewModel {
  isLoading = false;

  payments: any = [];

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);

    reaction(
      () => this.period,
      async () => {
        if (!this.period[0] || !this.period[1]) return;

        await this.loadPayments();
      },
      { fireImmediately: true }
    );
  }

  private _dateBegin: any = new Date();

  get dateBegin(): any {
    return this._dateBegin;
  }

  set dateBegin(value: any) {
    this._dateBegin = value;
    this.period = [moment(value).startOf('month').toDate(), moment(this.dateEnd).endOf('month').toDate()];
  }

  private _dateEnd: any = new Date();

  period: any = [moment(this._dateBegin).startOf('month').toDate(), moment(this._dateEnd).endOf('month').toDate()];

  get dateEnd(): any {
    return this._dateEnd;
  }

  set dateEnd(value: any) {
    this._dateEnd = value;
    this.period = [moment(this.dateBegin).startOf('month').toDate(), moment(value).endOf('month').toDate()];
  }

  get rows() {
    return this.payments?.map((e: any, idx: number) => ({ id: idx, ...e })) || [];
  }

  downloadReport = async (visibleRowIds: number[]): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');

      this.isLoading = true;

      const begin = moment.utc(this.period[0]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
      const end = moment.utc(this.period[1]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]');

      // OSI-808: id строки грида — это индекс в this.payments (см. rows: id: idx выше), поэтому
      // по visibleRowIds достаём ИСХОДНЫЕ объекты платежей. Без фильтра/сортировки в гриде
      // visibleRowIds содержит все id по порядку — путь один и тот же, отдельной ветки
      // "фильтра нет — грузим всё как раньше" не требуется.
      // Явно перечисляем поля контракта (а не спред всего объекта) — на случай, если платёж от
      // OSI.Core содержит служебные поля, которых не должно быть в теле запроса.
      const filteredPayments = visibleRowIds
        .map((id) => this.payments[id])
        .filter(Boolean)
        .map(({ dt, abonentId, abonentName, flat, flatDisplay, serviceName, amount, bankName }: any) => ({
          dt,
          abonentId,
          abonentName,
          flat,
          flatDisplay,
          serviceName,
          amount,
          bankName
        }));

      // OSI-808: отправляем СЫРЫЕ значения из vm.payments, а не то, что показывает грид после
      // valueFormatter/valueGetter (даты и суммы там отформатированы для отображения) — иначе
      // формат итогового Excel-документа изменится по сравнению с прежним поведением.
      const data = await api.getPaymentsReportFiltered(this.osiModule.osiId, begin, end, filteredPayments);

      const house = this.osiModule.osiInfo?.address
        ?.split(',')
        .slice(-1)
        .pop()
        ?.replace('д.', '')
        .replace('дом', '')
        .trim();
      const osiName = this.osiModule.osiInfo?.name.startsWith(this.osiModule.osiInfo?.unionTypeRu ?? '')
        ? this.osiModule.osiInfo?.name
        : `${this.osiModule.osiInfo?.unionTypeRu} ${this.osiModule.osiInfo?.name}`;
      // iOS/WebKit: data:-URL не скачивается по атрибуту download — переведено на blob: через downloadBase64
      downloadBase64(
        data as unknown as string,
        MIME.XLSX,
        `Реестр_платежей_${osiName}_дом_${house}_${this.period[0].toISOString().substring(0, 10)}_${this.period[1]
          .toISOString()
          .substring(0, 10)}.xlsx`
      );
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  loadPayments = async (): Promise<void> => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');

      this.isLoading = true;

      const queryOptions = getGetApiTransactionsPaymentsOsiIdQueryOptions(
        this.osiModule.osiId,
        {
          dateBegin: moment.utc(this.period[0]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
          dateEnd: moment.utc(this.period[1]).local().format('YYYY-MM-DD[T]HH:mm:ss[Z]')
        },
        { query: { gcTime: 0, staleTime: 0 } }
      );

      const response = await queryClient.fetchQuery(queryOptions);

      if (response.code !== 0) {
        throw new Error(response.message ?? 'Ошибка при запросе платежей');
      }

      const sortedResp = response.result?.sort((a, b) => {
        if (!a.dt || !b.dt) return 0;

        if (a.dt < b.dt) return -1;
        if (a.dt > b.dt) return 1;

        return 0;
      });
      this.payments = sortedResp || [];
    } catch (e: unknown) {
      logger.error(e);
      if (axios.isAxiosError(e)) {
        notifications.error(e.message);
      }
    } finally {
      this.isLoading = false;
    }
  };
}
